import { makeRequest } from "@/util";

import type {
  Survey,
  SurveyAppearance,
  SurveyTrigger,
  UrlMatchType,
  Question,
  SurveyResultsData,
  QuestionResponseSummary,
  SurveyStatus,
  QuestionType,
  DistributionData,
  RatingData,
  OpenTextData,
  NpsData,
  SurveyDateRange,
  SurveyTimeseries,
  SurveyTimeseriesGranularity,
  SurveyResponsesPage,
} from "./types";
import { DEFAULT_SURVEY_APPEARANCE } from "./types";

// These mirror the JSON shapes returned by the backend. They are
// intentionally kept local to this file — consumers use the
// frontend Survey/Question types from ./types.ts.
type BackendQuestionOptions =
  | { type: "single_choice"; choices: string[] }
  | { type: "open_text"; placeholder: string }
  | { type: "rating"; lowLabel: string; highLabel: string }
  | { type: "yes_no" }
  | { type: "nps" };

type BackendQuestion = {
  id: string;
  surveyId: string;
  position: number;
  type: QuestionType;
  text: string;
  options: BackendQuestionOptions;
  createdAt: string;
  updatedAt: string;
};

type BackendTrigger = {
  mode: "page" | "event";
  matchType: string;
  patterns: string[];
  eventNames: string[];
};

type BackendSurvey = {
  id: string;
  envId: string;
  name: string;
  description: string;
  status: SurveyStatus;
  version: number;
  trigger: BackendTrigger;
  idleTimeSeconds: number;
  startDate: string | null;
  endDate: string | null;
  appearance: SurveyAppearance | null;
  questions: BackendQuestion[];
  responseCount: number;
  createdAt: string;
  updatedAt: string;
  sourceConversationId: string | null;
};

type BackendQuestionSummary = {
  questionId: string;
  questionIndex: number;
  questionType: QuestionType;
  questionPrompt: string;
  totalResponses: number;
  data: unknown;
};

type BackendResults = {
  surveyId: string;
  impressions: number;
  responses: number;
  inProgress: number;
  responseRate: number | null;
  questionSummaries: BackendQuestionSummary[];
};

type BackendTimeseriesPoint = {
  bucket: string;
  impressions: number;
  responses: number;
  inProgress: number;
};

type BackendTimeseries = {
  granularity: SurveyTimeseriesGranularity;
  points: BackendTimeseriesPoint[];
};

type BackendResponseRow = {
  deviceId: string;
  userId: string | null;
  startedAt: string;
  completedAt: string | null;
  pageUrl: string | null;
  answers: Record<string, string>;
};

type BackendResponsesPage = {
  rows: BackendResponseRow[];
  total: number;
};

type GetSurveysResponse = { surveys: BackendSurvey[] };
type CreateSurveyResponse = { survey: BackendSurvey };
type UpdateSurveyResponse = { survey: BackendSurvey };
type DeleteSurveyResponse = { success: boolean };
type PublishSurveyResponse = { survey: BackendSurvey };
type ArchiveSurveyResponse = { survey: BackendSurvey };
type UnpublishSurveyResponse = { survey: BackendSurvey };
type GetSurveyResultsResponse = { results: BackendResults };
type GetSurveyTimeseriesResponse = BackendTimeseries;
type GetSurveyResponsesResponse = { page: BackendResponsesPage };

function parseBackendQuestion(raw: BackendQuestion): Question {
  const base = { id: raw.id, text: raw.text, position: raw.position };

  switch (raw.type) {
    case "single_choice": {
      const opts = raw.options as { type: "single_choice"; choices: string[] };
      return { ...base, type: "single_choice", options: opts.choices };
    }
    case "open_text": {
      const opts = raw.options as { type: "open_text"; placeholder: string };
      return { ...base, type: "open_text", placeholder: opts.placeholder };
    }
    case "rating": {
      const opts = raw.options as {
        type: "rating";
        lowLabel: string;
        highLabel: string;
      };
      return {
        ...base,
        type: "rating",
        scale: 5,
        lowLabel: opts.lowLabel,
        highLabel: opts.highLabel,
      };
    }
    case "yes_no":
      return { ...base, type: "yes_no" };
    case "nps":
      return { ...base, type: "nps" };
  }
}

const KNOWN_MATCH_TYPES: ReadonlySet<string> = new Set<UrlMatchType>([
  "contains",
  "exact",
  "starts_with",
  "wildcard",
]);

function coerceMatchType(
  raw: string | undefined,
  surveyId: string,
): UrlMatchType {
  if (!raw) return "contains";
  if (KNOWN_MATCH_TYPES.has(raw)) return raw as UrlMatchType;
  // Unknown values silently coerce to "contains" so the UI stays
  // usable, but log so we notice if the backend ever ships a new
  // matchType the frontend doesn't recognize. Mirrors the
  // `normalizeSurveyTrigger` warning in the SDK.
  console.warn(
    `Freshpaint: unknown trigger.matchType "${raw}" for survey ${surveyId}; ` +
      `falling back to "contains".`,
  );
  return "contains";
}

function parseBackendTrigger(raw: BackendSurvey): SurveyTrigger {
  // The backend always emits `trigger` (CE-1267 D2 removed all legacy
  // scalars). A missing payload here is a contract violation; default
  // to an empty page-mode trigger so the UI renders an editable draft
  // instead of crashing, and warn so we notice the regression.
  if (!raw.trigger) {
    console.warn(
      `Freshpaint: survey ${raw.id} has no trigger payload from the backend; ` +
        `defaulting to an empty page trigger. This indicates a contract violation.`,
    );
    return {
      kind: "page",
      pageConstraint: { matchType: "contains", patterns: [] },
    };
  }

  const matchType = coerceMatchType(raw.trigger.matchType, raw.id);
  const patterns = raw.trigger.patterns ?? [];

  if (raw.trigger.mode === "event") {
    return {
      kind: "event",
      eventNames: raw.trigger.eventNames ?? [],
      pageConstraint: { matchType, patterns },
    };
  }

  return {
    kind: "page",
    pageConstraint: { matchType, patterns },
  };
}

function parseBackendSurvey(raw: BackendSurvey): Survey {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description,
    status: raw.status,
    questions: raw.questions.map(parseBackendQuestion),
    trigger: parseBackendTrigger(raw),
    idleTimeSeconds: raw.idleTimeSeconds,
    startDate: raw.startDate ? new Date(raw.startDate) : null,
    endDate: raw.endDate ? new Date(raw.endDate) : null,
    // Backend always populates appearance (default-backfilled in the
    // migration) but defensively fall back to the FE default so older
    // payloads or partial fixtures still render correctly.
    appearance: raw.appearance ?? DEFAULT_SURVEY_APPEARANCE,
    responseCount: raw.responseCount,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    sourceConversationId: raw.sourceConversationId ?? null,
  };
}

function parseBackendResults(raw: BackendResults): SurveyResultsData {
  return {
    surveyId: raw.surveyId,
    impressions: raw.impressions,
    responses: raw.responses,
    inProgress: raw.inProgress ?? 0,
    responseRate: raw.responseRate ?? null,
    questionSummaries: raw.questionSummaries.map(
      (qs): QuestionResponseSummary => {
        const base = {
          questionId: qs.questionId,
          questionIndex: qs.questionIndex,
          questionPrompt: qs.questionPrompt,
          totalResponses: qs.totalResponses,
        };

        switch (qs.questionType) {
          case "single_choice":
          case "yes_no":
            return {
              ...base,
              questionType: qs.questionType,
              data: qs.data as DistributionData,
            };
          case "rating":
            return {
              ...base,
              questionType: "rating",
              data: qs.data as RatingData,
            };
          case "open_text":
            return {
              ...base,
              questionType: "open_text",
              data: qs.data as OpenTextData,
            };
          case "nps":
            return { ...base, questionType: "nps", data: qs.data as NpsData };
        }
      },
    ),
  };
}

function serializeQuestionForBackend(q: Question) {
  const base = { type: q.type, text: q.text };

  switch (q.type) {
    case "single_choice":
      return {
        ...base,
        options: { type: "single_choice" as const, choices: q.options },
      };
    case "open_text":
      return {
        ...base,
        options: { type: "open_text" as const, placeholder: q.placeholder },
      };
    case "rating":
      return {
        ...base,
        options: {
          type: "rating" as const,
          lowLabel: q.lowLabel,
          highLabel: q.highLabel,
        },
      };
    case "yes_no":
      return { ...base, options: { type: "yes_no" as const } };
    case "nps":
      return { ...base, options: { type: "nps" as const } };
  }
}

export type CreateSurveyParams = Omit<
  Survey,
  "id" | "createdAt" | "updatedAt" | "responseCount" | "status"
>;

export type UpdateSurveyParams = Partial<
  Omit<Survey, "id" | "createdAt" | "updatedAt" | "responseCount" | "status">
>;

// INTENTIONAL OMISSION:
//   - `responseLimit` (mode + maxResponses): exposed on the Behavior
//     step UI but the backend doesn't persist it yet. Tracked separately
//     from CE-1268.
// `appearance` is fully round-tripped now (CE-1268). `position` (legacy
// top-level field) is dropped — `appearance.position` is the source of
// truth.
function serializeTrigger(trigger: SurveyTrigger) {
  if (trigger.kind === "event") {
    return {
      mode: "event" as const,
      matchType: trigger.pageConstraint.matchType,
      patterns: trigger.pageConstraint.patterns,
      eventNames: trigger.eventNames,
    };
  }
  return {
    mode: "page" as const,
    matchType: trigger.pageConstraint.matchType,
    patterns: trigger.pageConstraint.patterns,
    eventNames: [],
  };
}

function serializeSurveyForCreate(data: CreateSurveyParams) {
  return {
    name: data.name,
    description: data.description,
    questions: data.questions.map(serializeQuestionForBackend),
    trigger: serializeTrigger(data.trigger),
    idleTimeSeconds: data.idleTimeSeconds,
    startDate: data.startDate ? data.startDate.toISOString() : null,
    endDate: data.endDate ? data.endDate.toISOString() : null,
    appearance: data.appearance,
    sourceConversationId: data.sourceConversationId ?? undefined,
  };
}

function serializeSurveyForUpdate(data: UpdateSurveyParams) {
  return {
    ...(data.name !== undefined && { name: data.name }),
    ...(data.description !== undefined && { description: data.description }),
    ...(data.questions !== undefined && {
      questions: data.questions.map(serializeQuestionForBackend),
    }),
    ...(data.trigger !== undefined && {
      trigger: serializeTrigger(data.trigger),
    }),
    ...(data.idleTimeSeconds !== undefined && {
      idleTimeSeconds: data.idleTimeSeconds,
    }),
    ...(data.startDate !== undefined && {
      startDate: data.startDate ? data.startDate.toISOString() : null,
    }),
    ...(data.endDate !== undefined && {
      endDate: data.endDate ? data.endDate.toISOString() : null,
    }),
    ...(data.appearance !== undefined && {
      appearance: data.appearance,
    }),
  };
}

export async function fetchSurveys(): Promise<Survey[]> {
  const res = await makeRequest<GetSurveysResponse>(
    "get_surveys",
    {},
    {
      validateStatus: (status) => {
        // 404 statuses are harmless. They might arise when the user doesn't
        // have access to the Freshpaint AI feature.
        return (status >= 200 && status < 300) || status === 404;
      },
    },
  );
  const surveys = Array.isArray(res.data?.surveys) ? res.data.surveys : [];
  return surveys.map(parseBackendSurvey);
}

export async function createSurvey(data: CreateSurveyParams): Promise<Survey> {
  const res = await makeRequest<CreateSurveyResponse>(
    "create_survey",
    serializeSurveyForCreate(data),
  );
  return parseBackendSurvey(res.data.survey);
}

export async function updateSurvey(
  surveyId: string,
  data: UpdateSurveyParams,
): Promise<Survey> {
  const res = await makeRequest<UpdateSurveyResponse>("update_survey", {
    surveyId,
    ...serializeSurveyForUpdate(data),
  });
  return parseBackendSurvey(res.data.survey);
}

export async function deleteSurvey(surveyId: string): Promise<void> {
  await makeRequest<DeleteSurveyResponse>("delete_survey", { surveyId });
}

export async function publishSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<PublishSurveyResponse>("publish_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

export async function archiveSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<ArchiveSurveyResponse>("pause_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

export async function unpublishSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<UnpublishSurveyResponse>("unpublish_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

// Serialize an optional date range into the request payload. Both bounds
// must be present (the backend rejects partial ranges) and dates are
// transmitted as ISO-8601 strings in UTC.
function serializeDateRange(range: SurveyDateRange | null) {
  if (!range) return {};
  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  };
}

export async function fetchSurveyResults(
  surveyId: string,
  dateRange: SurveyDateRange | null = null,
): Promise<SurveyResultsData> {
  const res = await makeRequest<GetSurveyResultsResponse>(
    "get_survey_results",
    { surveyId, ...serializeDateRange(dateRange) },
  );
  return parseBackendResults(res.data.results);
}

export async function fetchSurveyTimeseries(
  surveyId: string,
  dateRange: SurveyDateRange | null = null,
): Promise<SurveyTimeseries> {
  const res = await makeRequest<GetSurveyTimeseriesResponse>(
    "get_survey_timeseries",
    { surveyId, ...serializeDateRange(dateRange) },
  );
  return {
    granularity: res.data.granularity,
    points: res.data.points.map((p) => ({
      bucket: new Date(p.bucket),
      impressions: p.impressions,
      responses: p.responses,
      inProgress: p.inProgress ?? 0,
    })),
  };
}

export async function fetchSurveyResponses(
  surveyId: string,
  dateRange: SurveyDateRange | null = null,
  pagination: { limit: number; offset: number } = { limit: 50, offset: 0 },
): Promise<SurveyResponsesPage> {
  const res = await makeRequest<GetSurveyResponsesResponse>(
    "get_survey_responses",
    {
      surveyId,
      limit: pagination.limit,
      offset: pagination.offset,
      ...serializeDateRange(dateRange),
    },
  );
  return {
    total: res.data.page.total,
    rows: res.data.page.rows.map((r) => ({
      deviceId: r.deviceId,
      userId: r.userId,
      startedAt: new Date(r.startedAt),
      completedAt: r.completedAt ? new Date(r.completedAt) : null,
      pageUrl: r.pageUrl,
      answers: r.answers ?? {},
    })),
  };
}
